import { useEffect, useMemo, useState } from 'react';
import authService from '../../services/auth.service';
import schedulingService, { type TimeOffBlock } from '../../services/scheduling.service';
import managerService, { type ManagerAgent } from '../../services/manager.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { toLocalInput } from '../../lib/datetime';
import { fullName } from '../../lib/fullName';
import { colors } from '../../styles/theme';
import { timeOffDeleteWarning } from './timeOff.helpers';
import { useMovablePopup } from '../MovablePopup';
import { readerLocale } from '../../i18n/language';

interface Props {
  /**
   * 'self'    — agent managing their own days off (no picker; component
   *             resolves the agent's UUID from /auth/me and hits
   *             /agents/{id}/time-off like manager mode would).
   * 'manager' — manager managing on behalf of any agent (picker + the
   *             same /agents/{id}/time-off endpoints).
   */
  mode: 'self' | 'manager';
  /** Optional initial pick for manager mode (e.g., when the trigger
   *  already knows which agent — like a "Days off" button on a row). */
  initialAgentId?: string | null;
  initialAgentName?: string | null;
  onClose: () => void;
}

const MAX_REASON = 500;

function formatBlockRange(startsIso: string, endsIso: string): string {
  try {
    const s = new Date(startsIso);
    const e = new Date(endsIso);
    const sameDay = s.toDateString() === e.toDateString();
    const dateOpts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    const timeOpts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
    if (sameDay) {
      return `${s.toLocaleDateString(readerLocale(), dateOpts)} · ${s.toLocaleTimeString(readerLocale(), timeOpts)} – ${e.toLocaleTimeString(readerLocale(), timeOpts)}`;
    }
    return `${s.toLocaleString(readerLocale(), { ...dateOpts, ...timeOpts })} – ${e.toLocaleString(readerLocale(), { ...dateOpts, ...timeOpts })}`;
  } catch {
    return `${startsIso} – ${endsIso}`;
  }
}

/**
 * Compact modal for managing an agent's days off. Same component covers
 * both the agent's self-management UI and the manager's "do it for an
 * agent" flow — driven by the `mode` prop. The form intentionally stays
 * narrow (max-w-md) and small so it fits as a low-frequency utility.
 */
export default function TimeOffManager({
  mode,
  initialAgentId,
  initialAgentName,
  onClose,
}: Readonly<Props>): JSX.Element {
  const popup = useMovablePopup<HTMLDivElement>();
  // Self mode skips the picker but still needs the agent's UUID to call
  // `/agents/{id}/time-off` (the only surface — `/me/time-off` aliases
  // were removed). We resolve it from /auth/me in a useEffect below;
  // until that resolves, selectedAgentId stays null and the list/form
  // render their loading state.
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(
    mode === 'self' ? null : (initialAgentId ?? null),
  );
  const [selectedAgentName, setSelectedAgentName] = useState<string | null>(initialAgentName ?? null);
  const [agents, setAgents] = useState<ManagerAgent[] | null>(null);

  const [blocks, setBlocks] = useState<TimeOffBlock[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formStartsAt, setFormStartsAt] = useState<string>('');
  const [formEndsAt, setFormEndsAt] = useState<string>('');
  const [formReason, setFormReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // Default the form to "tomorrow 9 AM → tomorrow 6 PM" so the agent /
  // manager doesn't have to type ISO datetimes from scratch every time.
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    const end = new Date(tomorrow);
    end.setHours(18, 0, 0, 0);
    setFormStartsAt(toLocalInput(tomorrow));
    setFormEndsAt(toLocalInput(end));
  }, []);

  // Self mode: resolve the caller's UUID from /auth/me so we can hit
  // the unified /agents/{id}/time-off surface. We do this once on
  // mount; the cached endpoint typically returns in <50ms.
  useEffect(() => {
    if (mode !== 'self') return;
    let cancelled = false;
    authService.getCurrentUser()
      .then((me) => {
        if (cancelled) return;
        setSelectedAgentId(me.id);
        setSelectedAgentName(`${me.first_name} ${me.last_name}`.trim() || null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(getApiErrorMessage(err, 'Could not resolve your account.'));
      });
    return () => { cancelled = true; };
  }, [mode]);

  // Manager mode: load the agent picker on mount.
  useEffect(() => {
    if (mode !== 'manager') return;
    let cancelled = false;
    managerService.listAgents()
      .then((data) => { if (!cancelled) setAgents(data); })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(getApiErrorMessage(err, 'Could not load agents.'));
      });
    return () => { cancelled = true; };
  }, [mode]);

  // Whenever an agent is selected (or always, in self mode), refresh
  // their existing blocks.
  useEffect(() => {
    if (!selectedAgentId) {
      setBlocks(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    schedulingService.listAgentTimeOff(selectedAgentId)
      .then((data) => { if (!cancelled) setBlocks(data); })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(getApiErrorMessage(err, 'Could not load days off.'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mode, selectedAgentId]);

  // ESC closes when not mid-submit
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  function handlePickAgent(agentId: string): void {
    const agent = agents?.find((a) => a.id === agentId) ?? null;
    setSelectedAgentId(agentId || null);
    setSelectedAgentName(agent ? fullName(agent) : null);
    setFlash(null);
    setSubmitError(null);
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!selectedAgentId) {
      setSubmitError('Please pick an agent first.');
      return;
    }
    setSubmitError(null);
    setFlash(null);

    // Convert the local datetime input back to ISO. The input value is
    // "YYYY-MM-DDTHH:MM" in the browser's local TZ; new Date() interprets
    // it that way, then toISOString gives UTC.
    const start = new Date(formStartsAt);
    const end = new Date(formEndsAt);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setSubmitError('Invalid start or end time.');
      return;
    }
    if (start >= end) {
      setSubmitError('End must be after start.');
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        starts_at: start.toISOString(),
        ends_at: end.toISOString(),
        reason: formReason.trim() || null,
      };
      const res = await schedulingService.createAgentTimeOff(selectedAgentId, body);
      const updated = await schedulingService.listAgentTimeOff(selectedAgentId);
      setBlocks(updated);
      const orphanCount = res.orphaned_appointment_ids.length;
      const orphanPlural = orphanCount === 1 ? '' : 's';
      const orphanMsg = orphanCount > 0
        ? ` ${orphanCount} appointment${orphanPlural} moved to triage queue.`
        : '';
      setFlash(`Day off saved.${orphanMsg}`);
      setFormReason('');
    } catch (err: unknown) {
      setSubmitError(getApiErrorMessage(err, 'Could not save the day off.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(blockId: string): Promise<void> {
    if (!selectedAgentId) return;
    if (!globalThis.confirm(timeOffDeleteWarning(mode))) return;
    try {
      await schedulingService.deleteAgentTimeOff(selectedAgentId, blockId);
      const updated = await schedulingService.listAgentTimeOff(selectedAgentId);
      setBlocks(updated);
      setFlash('Day off removed.');
    } catch (err: unknown) {
      setLoadError(getApiErrorMessage(err, 'Could not remove the day off.'));
    }
  }

  const sortedBlocks = useMemo(() => {
    if (!blocks) return [];
    return [...blocks].sort((a, b) =>
      new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime()
    );
  }, [blocks]);

  return (
    <dialog
      open
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: 'rgba(6,9,26,0.55)',
        margin: 0,
        border: 'none',
        maxWidth: '100%',
        maxHeight: '100%',
        width: '100%',
        height: '100%',
      }}
      aria-modal="true"
      aria-labelledby="timeoff-modal-title"
    >
      <div
        ref={popup.panelRef}
        className="w-full max-w-lg rounded-2xl p-6 overflow-y-auto"
        style={{ ...popup.panelStyle, background: colors.surface, maxHeight: '90vh' }}
      >
        {popup.bar}
        <h2 id="timeoff-modal-title" className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
          Days off
        </h2>
        <p className="text-sm mb-4" style={{ color: colors.muted }}>
          {mode === 'manager'
            ? 'Add or remove time-off blocks on behalf of an agent. Overlapping appointments are auto-orphaned and moved to the triage queue.'
            : 'Add or remove your own time-off blocks. Overlapping appointments are auto-orphaned so other agents can pick them up.'}
        </p>

        {/* Agent picker (manager mode only) */}
        {mode === 'manager' && (
          <label className="block text-sm mb-4">
            <span className="block mb-1 font-medium" style={{ color: colors.ink }}>Agent</span>
            <select
              value={selectedAgentId ?? ''}
              onChange={(e) => handlePickAgent(e.target.value)}
              disabled={agents === null}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ border: `1px solid ${colors.border}`, color: colors.ink, background: colors.surface }}
            >
              <option value="">{agents === null ? 'Loading agents…' : 'Select an agent'}</option>
              {agents
                ? [...agents]
                    .sort((a, b) => fullName(a).localeCompare(fullName(b)))
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {fullName(a)} · {a.pending_count} pending
                      </option>
                    ))
                : null}
            </select>
          </label>
        )}

        {selectedAgentName && (
          <div className="text-xs mb-3" style={{ color: colors.mutedSoft }}>
            Managing time-off for <span className="font-medium" style={{ color: colors.ink }}>{selectedAgentName}</span>
          </div>
        )}

        {flash && (
          <output
            className="rounded-lg p-3 mb-3 text-sm flex items-start justify-between gap-3"
            style={{
              background: colors.successBg,
              color: colors.success,
              border: `1px solid #A7F3D0`,
            }}
          >
            <span>{flash}</span>
            <button type="button" onClick={() => setFlash(null)}
                    className="text-xs underline flex-shrink-0" style={{ color: colors.success }}>
              Dismiss
            </button>
          </output>
        )}

        {loadError && (
          <div className="rounded-lg p-3 mb-3 text-sm" role="alert"
               style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
            {loadError}
          </div>
        )}

        {/* Existing blocks list */}
        {selectedAgentId && (
          <div className="mb-4">
            <div className="text-xs font-bold uppercase mb-2" style={{ color: colors.muted }}>
              Existing blocks
            </div>
            {!loading && sortedBlocks.length > 0 && (
              <p className="text-xs mb-2" style={{ color: colors.mutedSoft }}>
                Removing a block does not bring back appointments it moved to triage —
                reclaim or reassign them in Appointments → Orphans.
              </p>
            )}
            {loading && <p className="text-sm" style={{ color: colors.mutedSoft }}>Loading…</p>}
            {!loading && sortedBlocks.length === 0 && (
              <p className="text-sm" style={{ color: colors.mutedSoft }}>None on file.</p>
            )}
            {!loading && sortedBlocks.length > 0 && (
              <ul className="space-y-2">
                {sortedBlocks.map((b) => (
                  <li
                    key={b.id}
                    className="flex items-start justify-between gap-3 p-2 rounded text-sm"
                    style={{ background: colors.canvas, border: `1px solid ${colors.borderSoft}` }}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium" style={{ color: colors.ink }}>
                        {formatBlockRange(b.starts_at, b.ends_at)}
                      </div>
                      {b.reason && (
                        <div className="text-xs mt-0.5" style={{ color: colors.mutedSoft }}>
                          {b.reason}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDelete(b.id)}
                      title="Remove"
                      aria-label="Remove this block"
                      className="px-2 py-1 rounded text-xs flex-shrink-0"
                      style={{ background: colors.surface, color: colors.danger, border: `1px solid ${colors.border}` }}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Add new block */}
        {selectedAgentId && (
          <form onSubmit={handleSubmit} className="border-t pt-4 space-y-3" style={{ borderColor: colors.border }}>
            <div className="text-xs font-bold uppercase" style={{ color: colors.muted }}>
              Add a new block
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="block mb-1 font-medium" style={{ color: colors.ink }}>Starts</span>
                <input
                  type="datetime-local"
                  value={formStartsAt}
                  min={new Date().toISOString().slice(0, 16)}
                  onChange={(e) => setFormStartsAt(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
                />
              </label>
              <label className="block text-sm">
                <span className="block mb-1 font-medium" style={{ color: colors.ink }}>Ends</span>
                <input
                  type="datetime-local"
                  value={formEndsAt}
                  min={formStartsAt || new Date().toISOString().slice(0, 16)}
                  onChange={(e) => setFormEndsAt(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
                />
              </label>
            </div>
            <label className="block text-sm">
              <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
                Reason <span className="font-normal" style={{ color: colors.mutedSoft }}>(optional, max {MAX_REASON} chars)</span>
              </span>
              <textarea
                value={formReason}
                onChange={(e) => setFormReason(e.target.value)}
                rows={2}
                maxLength={MAX_REASON}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
                placeholder="Vacation, training, half-day, …"
              />
            </label>

            {submitError && (
              <div className="rounded-lg p-3 text-sm" role="alert"
                   style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
                {submitError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} disabled={submitting}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}>
                Close
              </button>
              <button type="submit" disabled={submitting}
                      className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                      style={{
                        background: colors.navy, color: '#FFFFFF',
                        opacity: submitting ? 0.6 : 1,
                        cursor: submitting ? 'wait' : 'pointer',
                      }}>
                {submitting ? 'Saving…' : 'Add day off'}
              </button>
            </div>
          </form>
        )}

        {/* Footer when no agent picked yet (manager mode) */}
        {!selectedAgentId && mode === 'manager' && (
          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}>
              Close
            </button>
          </div>
        )}
      </div>
    </dialog>
  );
}
